import { useEffect, useState } from "react";
import { PROJECT_FIELDS } from "./constants";
import { FiChevronDown } from "react-icons/fi";
import { MdEdit, MdCheck, MdClose } from "react-icons/md";
import { ROLES } from "../../../Constants/Strings";
import {
  RoleValidator,
  SearchableDropdown,
  CompletionProgressBar,
} from "../../../Components/Common";
import { useClient } from "../../ClientMaster/Hooks/useClient";
import { motion, AnimatePresence } from "framer-motion";

const DetailsTab = ({ projectDetails, saveProjectDetails, loading }) => {
  const { fetchAllClients } = useClient();
  const [client, setClient] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isExpanded, setIsExpanded] = useState(true);

  const handleSelectClient = (client) => {
    setEditForm((prev) => ({
      ...prev,
      client: client?.id || null,
    }));
    setClient(client);
  };

  useEffect(() => {
    if (projectDetails?.client_details) {
      setClient(projectDetails.client_details);
    }

    // sync editForm when projectDetails changes (so edit form is current)
    setEditForm({
      ...projectDetails,
      client:
        projectDetails?.client_details?.id || projectDetails?.client || null,
    });
  }, [projectDetails]);

  const handleEditClick = () => {
    setEditForm({
      ...projectDetails,
      client:
        projectDetails?.client_details?.id || projectDetails?.client || null,
    });
    setClient(projectDetails?.client_details || {});
    setIsEditing(true);
  };

  const handleSave = async () => {
    const payload = {
      ...editForm,
      client: editForm.client || editForm.client_id || null,
    };

    const updated = await saveProjectDetails(payload);
    if (updated?.client_details) {
      setClient(updated.client_details);
    }
    setIsEditing(false);
  };

  return (
    <div className="relative overflow-visible z-10">
      <div
        className="flex flex-wrap items-center justify-between my-2 cursor-pointer select-none px-3 gap-3"
        onClick={() => {
          setIsExpanded((prev) => {
            const next = !prev;
            if(!next) setIsEditing(false);
            return next;
          });
        }}
      >
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-primary leading-snug break-words w-[45%]">
            {projectDetails?.name
              ? `${projectDetails.name} Project`
              : "Project Name"}
          </h3>

          <div className="w-[45%] min-w-[120px]">
            <CompletionProgressBar
              subtitle=""
              progress={projectDetails?.completion_percentage}
              color="bg-blue-500"
            />
          </div>
        </div>

        <button className="text-gray-600 transition-transform duration-300 flex-shrink-0">
          <FiChevronDown
            size={24}
            className={`transform transition-transform duration-200 ${
              isExpanded ? "rotate-180" : "rotate-0"
            }`}
          />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="details-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.25, 1] }}
            className="overflow-visible"
          >
            {loading ? (
              <div className="flex justify-center items-center h-[150px]">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
              </div>
            ) : projectDetails === null ? (
              <div className="text-center text-gray-500 py-6">
                No project details found.
              </div>
            ) : (
              <div className="relative p-4 overflow-visible">
                {/* Edit / Save buttons */}
                {isEditing ? (
                  <div className="absolute top-2 right-3 z-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSave();
                      }}
                      className="p-1"
                    >
                      <MdCheck size={22} className="text-green-500" />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(false);
                      }}
                      className="p-1"
                    >
                      <MdClose size={22} className="text-red-500" />
                    </button>
                  </div>
                ) : (
                  <RoleValidator
                    allowedRoles={[
                      ROLES.MANAGING_DIRECTOR,
                      ROLES.HEAD_OFFICE,
                      ROLES.GOVERNMENT_OFFICER,
                      ROLES.PROJECT_MANAGER,
                      ROLES.PROJECT_ENGINEER,
                    ]}
                    mode="disable"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick();
                      }}
                      className="absolute top-2 right-3 text-blue-600 z-1"
                    >
                      <MdEdit size={20} />
                    </button>
                  </RoleValidator>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full">
                  {PROJECT_FIELDS.map((field) => (
                    <div key={field.key}>
                      <h4 className="text-sm text-gray-500">{field.label}</h4>

                      {isEditing ? (
                        field.disabled ? (
                          <p className="text-sm font-semibold text-gray-400">
                            {projectDetails?.[field.key] || "-"}
                          </p>
                        ) : field.type === "select" ? (
                          <select
                            className="text-sm font-semibold pl-0 pt-0"
                            value={editForm[field.key] || ""}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                [field.key]: e.target.value,
                              })
                            }
                            style={{
                              border: "none",
                              borderBottom: "2px solid #CBD5E0",
                              outline: "none",
                              boxShadow: "none",
                              width: "fit-content",
                              minWidth: "120px",
                              maxWidth: "70%",
                            }}
                          >
                            {field.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type}
                            className="text-sm font-semibold pl-0 pt-0"
                            value={editForm[field.key] || ""}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                [field.key]: e.target.value,
                              })
                            }
                            style={{
                              border: "none",
                              borderBottom: "2px solid #CBD5E0",
                              outline: "none",
                              boxShadow: "none",
                              width: "fit-content",
                              minWidth: "120px",
                              maxWidth: "70%",
                            }}
                          />
                        )
                      ) : (
                        <p className="text-sm font-semibold">
                          {field.format
                            ? field.format(projectDetails?.[field.key])
                            : field.options
                            ? field.options.find(
                                (o) => o.value === projectDetails?.[field.key]
                              )?.label
                            : projectDetails?.[field.key]}
                        </p>
                      )}
                    </div>
                  ))}

                  {/* Client Section */}
                  <div>
                    <h4 className="text-sm text-gray-500">Client</h4>
                    {isEditing ? (
                      <SearchableDropdown
                        fetchFunction={fetchAllClients}
                        onSelect={handleSelectClient}
                        labelKey="name"
                        placeholder="Select client"
                        width="40"
                      />
                    ) : (
                      <p className="text-sm font-semibold">
                        {client?.name || "-"}
                      </p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm text-gray-500">Contact Person</h4>
                    <p className="text-sm font-semibold text-gray-800">
                      {client?.contact_person_name || "-"}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm text-gray-500">Phone</h4>
                    <p className="text-sm font-semibold text-gray-800">
                      {client?.mobile || "-"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DetailsTab;
